import axios from "axios";
import Cookies from "universal-cookie";

function getToken() {
  const cookies = new Cookies();
  const token = cookies.get("token");
  
  // Note: Laravel Sanctum tokens are not JWTs, so we skip format validation
  // Basic XSS protection only
  if (token && (token.includes('<script') || token.includes('javascript:'))) {
    console.warn('Suspicious token content detected, removing token');
    return null;
  }
  
  return token;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const getAuthHeaders = () => ({
  authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json",
});

// Task CRUD Operations
export const createTask = async (taskData) => {
  const response = await axios.post(`${API_BASE}/tasks`, taskData, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getTask = async (taskId) => {
  const response = await axios.get(`${API_BASE}/tasks/${taskId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updateTask = async (taskId, taskData) => {
  const response = await axios.put(`${API_BASE}/tasks/${taskId}`, taskData, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const deleteTask = async (taskId) => {
  const response = await axios.delete(`${API_BASE}/tasks/${taskId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const duplicateTask = async (taskId, overrides = {}) => {
  const response = await axios.post(`${API_BASE}/tasks/${taskId}/duplicate`, overrides, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Task Management Operations
export const assignTask = async (taskId, assigneeId) => {
  const response = await axios.patch(`${API_BASE}/tasks/${taskId}/assign`, 
    { assignee_id: assigneeId }, 
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const moveTaskToProject = async (taskId, projectId) => {
  const response = await axios.patch(`${API_BASE}/tasks/${taskId}/move-to-project`, 
    { project_id: projectId }, 
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const reorderTask = async (taskId, statusId, sortOrder) => {
  const response = await axios.patch(`${API_BASE}/tasks/${taskId}/reorder`, 
    { 
      status_id: statusId,
      sort_order: sortOrder 
    }, 
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Board Views
export const getUserTasks = async (userId) => {
  const response = await axios.get(`${API_BASE}/boards/user/${userId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getProjectTasks = async (projectId) => {
  const response = await axios.get(`${API_BASE}/boards/project/${projectId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getTeamTasks = async (teamId, filters = {}) => {
  const params = new URLSearchParams({ team_id: teamId, ...filters });
  const response = await axios.get(`${API_BASE}/tasks?${params}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getCalendarTasks = async (filters = {}) => {
  const params = new URLSearchParams(filters);
  const response = await axios.get(`${API_BASE}/boards/calendar?${params}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Task Statuses
export const getTaskStatuses = async (teamId) => {
  const response = await axios.get(`${API_BASE}/task-statuses?team_id=${teamId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Task Categories
export const getTaskCategories = async (teamId) => {
  const response = await axios.get(`${API_BASE}/task-categories?team_id=${teamId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createTaskCategory = async (categoryData) => {
  const response = await axios.post(`${API_BASE}/task-categories`, categoryData, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updateTaskCategory = async (categoryId, categoryData) => {
  const response = await axios.put(`${API_BASE}/task-categories/${categoryId}`, categoryData, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const deleteTaskCategory = async (categoryId) => {
  const response = await axios.delete(`${API_BASE}/task-categories/${categoryId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Task Comments
export const deleteTaskComment = async (commentId) => {
  const response = await axios.delete(`${API_BASE}/task-comments/${commentId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

// Task Files
export const getTaskFiles = async (taskId) => {
  const response = await axios.get(`${API_BASE}/tasks/${taskId}/files`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const uploadTaskFile = async (taskId, fileData) => {
  const formData = new FormData();
  formData.append('file', fileData.file);
  if (fileData.description) {
    formData.append('description', fileData.description);
  }

  const response = await axios.post(`${API_BASE}/tasks/${taskId}/files`, formData, {
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateTaskFile = async (taskId, fileId, data) => {
  const response = await axios.put(`${API_BASE}/tasks/${taskId}/files/${fileId}`, data, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const deleteTaskFile = async (taskId, fileId) => {
  const response = await axios.delete(`${API_BASE}/tasks/${taskId}/files/${fileId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};